// Hybrid logical clock (docs/architecture/sync-system.md §4.3): a single
// sortable value that orders edits and resolves same-field conflicts
// deterministically. Wire format is the fixed-width, lexicographically
// sortable string "{physicalMs:12 hex}-{counter:4 hex}-{nodeId:4 hex}".

import type { Hlc } from "@/types";

const MILLIS_WIDTH = 12;
const COUNTER_WIDTH = 4;
const NODE_WIDTH = 4;
const COUNTER_MAX = 0xffff;
const LAST_SEEN_KEY = "a8.hlcLastSeen";
const DEVICE_ID_KEY = "a8.deviceId";

/** A stable per-browser device id (also the HLC node source). */
export function deviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const fresh = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_KEY, fresh);
  return fresh;
}

/** 16-bit FNV-1a hash of the device id — the HLC node id. */
function nodeId(): number {
  let hash = 2166136261;
  for (const byte of new TextEncoder().encode(deviceId())) {
    hash ^= byte;
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return (hash ^ (hash >>> 16)) & 0xffff;
}

function format(millis: number, counter: number, node: number): Hlc {
  return [
    millis.toString(16).padStart(MILLIS_WIDTH, "0"),
    counter.toString(16).padStart(COUNTER_WIDTH, "0"),
    node.toString(16).padStart(NODE_WIDTH, "0"),
  ].join("-");
}

function parse(hlc: Hlc): { millis: number; counter: number } | null {
  const [millisPart, counterPart, nodePart] = hlc.split("-");
  if (!millisPart || !counterPart || !nodePart) return null;
  const millis = parseInt(millisPart, 16);
  const counter = parseInt(counterPart, 16);
  return Number.isFinite(millis) && Number.isFinite(counter) ? { millis, counter } : null;
}

/**
 * Mints strictly increasing clocks for this browser's edits and absorbs every
 * clock seen on pulled records, so a causally-later edit always outranks what
 * this client has already observed — regardless of wall-clock skew. The
 * last-seen value persists across reloads to keep clocks monotone.
 */
export class HlcClock {
  private lastSeen: Hlc;
  /** serverTime - clientTime, learned from every push response. */
  private serverOffsetMillis = 0;
  private readonly node = nodeId();

  constructor() {
    this.lastSeen = localStorage.getItem(LAST_SEEN_KEY) ?? format(0, 0, this.node);
  }

  /** The clock for a local edit happening now. */
  now(): Hlc {
    const physical = Math.max(0, Date.now() + this.serverOffsetMillis);
    const last = parse(this.lastSeen) ?? { millis: 0, counter: 0 };
    let next: Hlc;
    if (physical > last.millis) {
      next = format(physical, 0, this.node);
    } else if (last.counter < COUNTER_MAX) {
      next = format(last.millis, last.counter + 1, this.node);
    } else {
      next = format(last.millis + 1, 0, this.node);
    }
    this.remember(next);
    return next;
  }

  /** Absorb a clock seen on incoming data (string compare = clock compare). */
  observe(other: Hlc): void {
    if (other > this.lastSeen) this.remember(other);
  }

  /** Update the client→server clock offset from a push response's serverTime. */
  updateServerOffset(serverTimeIso: string): void {
    const serverMillis = Date.parse(serverTimeIso);
    if (Number.isFinite(serverMillis)) {
      this.serverOffsetMillis = serverMillis - Date.now();
    }
  }

  private remember(hlc: Hlc): void {
    this.lastSeen = hlc;
    localStorage.setItem(LAST_SEEN_KEY, hlc);
  }
}
